import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  SaveFileValue,
  TUploadFileResponse,
} from "../../../services/api/instances/saveFileApi/types";
import {
  fileUpload,
  fileUploadWithFormData,
  getFileByHash,
  getFileContentByHash,
  getFileTextByHash,
  getFileUrlByHash,
} from "../../../services/api/instances/saveFileApi/methods";
import { addStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import { TDataFile, TFileTypeConfig } from "../types";
import { downloadFileWithUrl, hasFile } from "../helpers";
import { createNotification } from "../../../app/state/notificationSlice/slice";
import { getError } from "../../../services/lib/getError";
import axios from "axios";
import { RootState } from "../../../services/redux/types";
import { MAX_STORED_LENGTH } from "../constants";

export const uploadFileAction = createAsyncThunk(
  "files/uploadFile",
  async (
    {
      data,
      targetObjectId,
    }: { data: TDataFile[]; targetObjectId?: string | null },
    ThunkAPI,
  ) => {
    try {
      let uploadedFile: PromiseSettledResult<TUploadFileResponse>[] = [];
      let storageValue: SaveFileValue[] = [];
      let objectIds: string[] = [];
      if (data.every(hasFile)) {
        uploadedFile = await Promise.allSettled(
          data.map((obj) => fileUploadWithFormData(obj.file, obj.file_name)),
        );
      } else {
        uploadedFile = await Promise.allSettled(
          data.map((obj) => {
            if (obj.url && obj.url.trim() !== "") {
              return fileUpload({
                url: obj.url,
                filename: obj.file_name,
              });
            } else if (obj.base64_content && obj.base64_content.trim() !== "") {
              return fileUpload({
                base64_content: obj.base64_content,
                filename: obj.file_name,
              });
            } else {
              return Promise.reject(new Error("No valid upload data provided"));
            }
          }),
        );
      }

      for (let index = 0; index < uploadedFile.length; index++) {
        const result = uploadedFile[index];
        const fileName = data[index].file_name ?? "file";

        if (result.status === "rejected") {
          ThunkAPI.dispatch(
            createNotification(
              "warning",
              `Ошибка загрузки файла: ${result.reason}`,
            ),
          );
          console.error(result.reason);
        } else {
          const value: SaveFileValue = {
            userfiles: {
              hash: result.value.hash,
              filename: fileName,
              metadata: {
                admin_only: false,
              },
            },
          };

          storageValue.push(value);

          const newObject = await ThunkAPI.dispatch(
            addStorageObject({
              name: fileName,
              service: "userfiles",
              objectType: "child",
              value,
              withoutCount: true,
              currentObjectId: targetObjectId ? targetObjectId : null,
            }),
          ).unwrap();

          objectIds.push(newObject?.id ?? "");
        }
      }
      const results = { value: storageValue.at(-1), id: objectIds.at(-1) };
      return results;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getFileContentAction = createAsyncThunk(
  "files/getFileContent",
  async (
    {
      hash,
      fileConfig,
      fileName,
    }: { hash: string; fileConfig: TFileTypeConfig; fileName?: string },
    { signal, getState, rejectWithValue },
  ) => {
    try {
      const value = (getState() as RootState).filesState.filesValue;
      if (signal.aborted) return rejectWithValue("aborted");

      const res = await getFileContentByHash(
        hash,
        fileConfig,
        fileName ? fileName : (value?.userfiles?.filename ?? ""),
        signal,
      );
      const fileContent = {
        ...fileConfig,
        fileContent: res?.slice(0, MAX_STORED_LENGTH) ?? "",
      };
      return fileContent;
    } catch (e) {
      if (
        signal.aborted ||
        axios.isCancel(e) ||
        (e instanceof Error && e.name === "AbortError")
      ) {
        return rejectWithValue("Aborted");
      }
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const downloadArtifactAction = createAsyncThunk(
  "files/downloadArtifact",
  async (_, { getState, rejectWithValue }) => {
    try {
      const value = (getState() as RootState).filesState.filesValue;
      const { hash, filename } = value?.userfiles ?? {};
      if (!hash || !filename) {
        return rejectWithValue("File hash or filename is missing");
      }
      const response = await getFileUrlByHash(hash, filename);
      downloadFileWithUrl(response.url, filename);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getArtifactContentAction = createAsyncThunk(
  "files/getArtifactContent",
  async (
    { hash, fileName }: { hash: string; fileName: string },
    { rejectWithValue },
  ) => {
    try {
      return getFileTextByHash(hash, fileName);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getArtifactFileAction = createAsyncThunk(
  "files/getArtifactFile",
  async (
    { hash, fileName }: { hash: string; fileName: string },
    { rejectWithValue },
  ) => {
    try {
      return getFileByHash(hash, fileName);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
